import React, { useState, useEffect } from "react";
import { axiosPost } from "../apis/useAxios";
import { Icon } from "@iconify/react";
import { useLocation } from "react-router-dom";
import { LoginAssetType } from "../helper/fixcode";
import { LogoutLoader } from "./Shared/PageLoader";
import TabStorage from "../helper/TabStorage";

const SideBarComponent = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadPanelVisible, setLoadPanelVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const user = TabStorage.getItem("user");
    setMenuItems(buildMenuItems(user?.featureList || []));
  }, []);

  useEffect(() => {
    document.body.classList.toggle("seg-collapsed", isCollapsed);
    return () => document.body.classList.remove("seg-collapsed");
  }, [isCollapsed]);

  useEffect(() => {
    const handler = () => setIsCollapsed((prev) => !prev);
    window.addEventListener("toggle-sidebar", handler);
    return () => window.removeEventListener("toggle-sidebar", handler);
  }, []);

  const buildMenuItems = (featureList) => {
    return featureList
      .filter((m) => m.subFeatureId === 0)
      .map((menu) => ({
        ...menu,
        children: featureList.filter((el) => el.subFeatureId === menu.id),
      }));
  };

  const isActive = (path) => {
    if (!path || path === "#") return false;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleOnLogout = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const userId = TabStorage.userId();
      const { lat, lng } = await TabStorage.getCurrentLocation();
      const response = await axiosPost(`/authenticate/user/${userId}/logout`, {
        SessionId: TabStorage.sessionId(),
        AssetTypeId: LoginAssetType.Web,
        Latitude: lat,
        Longitude: lng,
      });
      if (response.success) {
        window.ameyo.integration.doLogout();
      }
    } catch (error) {
      console.log(error);
    }
    TabStorage.clearSessionItems();
    window.location.href = "/";
  };

  const redirectToTracking = async (e) => {
    e.preventDefault();
    setLoadPanelVisible(true);
    try {
      const redirectdata = await axiosPost(
        process.env.REACT_APP_CUSTOMER_API_URL + "RedirectUser/external-token",
        { Source: "zenplus", Token: TabStorage.trackingGuid() }
      );
      if (redirectdata.isSuccess && redirectdata.payload != null) {
        window.open(
          process.env.REACT_APP_CUSTOMER_SITE_URL +
            "?userguid=" + TabStorage.trackingGuid() + "&source=zenplus",
          "_blank"
        );
      }
    } catch (err) {
      console.log("Error in redirectToTracking", err);
    }
    setLoadPanelVisible(false);
  };

  const renderMenuItem = (menu) => {
    const hasChildren = menu.children?.length > 0;
    const isOpen = openMenuId === menu.id;
    const name = menu.displayName;

    if (hasChildren) {
      const anyChildActive = menu.children.some((c) => isActive(c.requestPath));
      return (
        <div key={menu.id} className={`seg-nav-item${isOpen || anyChildActive ? " seg-open" : ""}`}>
          <div
            data-tooltip={name}
            className={`seg-nav-link seg-nav-group${anyChildActive ? " seg-active" : ""}`}
            onClick={() => setOpenMenuId(isOpen ? null : menu.id)}
          >
            <span className="seg-nav-icon">
              <Icon icon={menu.iconName} width="18" height="18" />
            </span>
            <span className="seg-nav-text">{name}</span>
            <i className={`fas fa-chevron-down seg-chevron${isOpen ? " seg-chevron-open" : ""}`} />
          </div>
          {isOpen && !isCollapsed && (
            <div className="seg-submenu">
              {menu.children.map((child) => (
                <div key={child.id} className="seg-nav-item">
                  <a
                    href={child.requestPath}
                    data-tooltip={child.displayName}
                    className={`seg-nav-link${isActive(child.requestPath) ? " seg-active" : ""}`}
                  >
                    <span className="seg-nav-icon">
                      <Icon icon={child.iconName || "carbon:circle-dash"} width="14" height="14" />
                    </span>
                    <span className="seg-nav-text">{child.displayName}</span>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // GPS Tracking
    if (name === "GPS Tracking") {
      if (TabStorage.trackingGuid() === "00000000-0000-0000-0000-000000000000") return null;
      return (
        <div key={menu.id} className="seg-nav-item">
          <a href="#" onClick={redirectToTracking} data-tooltip={name} className="seg-nav-link">
            <span className="seg-nav-icon"><Icon icon={menu.iconName} width="18" height="18" /></span>
            <span className="seg-nav-text">{name}</span>
          </a>
        </div>
      );
    }

    // Leads (force full reload)
    if (name === "Leads") {
      return (
        <div key={menu.id} className="seg-nav-item">
          <a
            href="/leads"
            data-tooltip={name}
            onClick={(e) => {
              e.preventDefault();
              window.history.replaceState(null, null, "/");
              window.location.href = "/leads";
            }}
            className={`seg-nav-link${isActive("/leads") ? " seg-active" : ""}`}
          >
            <span className="seg-nav-icon"><Icon icon={menu.iconName} width="18" height="18" /></span>
            <span className="seg-nav-text">{name}</span>
          </a>
        </div>
      );
    }

    // Ticket (external)
    if (name === "Ticket") {
      return (
        <div key={menu.id} className="seg-nav-item">
          <a href="https://ticketing.utwiz.com/" target="_new" data-tooltip={name} className="seg-nav-link">
            <span className="seg-nav-icon"><Icon icon={menu.iconName} width="18" height="18" /></span>
            <span className="seg-nav-text">{name}</span>
          </a>
        </div>
      );
    }

    // Default
    return (
      <div key={menu.id} className="seg-nav-item">
        <a
          href={menu.requestPath}
          data-tooltip={name}
          className={`seg-nav-link${isActive(menu.requestPath) ? " seg-active" : ""}`}
        >
          <span className="seg-nav-icon"><Icon icon={menu.iconName} width="18" height="18" /></span>
          <span className="seg-nav-text">{name}</span>
        </a>
      </div>
    );
  };

  return (
    <>
      <aside className={`seg-sidebar${isCollapsed ? " seg-sidebar--collapsed" : ""}`}>
        {/* Brand */}
        <div className="seg-brand">
          <a href="/" className="seg-brand-logo">
            <img
              src="./assets/img/Slattery-Energy-Group-Icon.png"
              alt="SEG"
              className="seg-brand-icon"
            />
            <img
              src="./assets/img/Slattery-Energy-Group-Light.png"
              alt="Slattery Energy Group"
              className="seg-brand-name"
            />
          </a>
        </div>

        {/* Nav */}
        <nav className="seg-sidebar-nav">
          {menuItems.map(renderMenuItem)}

          {/* Divider before logout */}
          <div className="seg-nav-divider" />

          {/* Logout */}
          <div className="seg-nav-item">
            <a href="#" data-tooltip="Logout" className="seg-nav-link seg-nav-logout" onClick={handleOnLogout}>
              <span className="seg-nav-icon">
                <Icon icon="material-symbols:logout" width="18" height="18" />
              </span>
              <span className="seg-nav-text">Logout</span>
            </a>
          </div>
        </nav>
      </aside>
      {isLoading && <LogoutLoader />}
    </>
  );
};

export default SideBarComponent;
